import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { 
  ChevronLeft, MapPin, User, Clock, MessageCircle, Star, 
  CheckCircle, Phone, Navigation, Loader2, XCircle, CreditCard,
  RefreshCw, AlertCircle
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import EmbeddedPayment from '@/components/EmbeddedPayment';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const WS_URL = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');

const JobDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const wsRef = useRef(null);
  
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [searchingAgain, setSearchingAgain] = useState(false);

  useEffect(() => {
    fetchJob();
    setupWebSocket();
    
    // Check payment status if returning from 3DS or processing
    const paymentParam = searchParams.get('payment');
    const intentId = searchParams.get('payment_intent');
    
    if (paymentParam === 'processing' && intentId) {
      checkPaymentStatus(intentId);
    }
    
    if (paymentParam === 'cancelled') {
      toast.error('Payment was cancelled');
    }
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [id]);

  const setupWebSocket = () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const wsUrl = `${WS_URL}/ws/${token}`;
    wsRef.current = new WebSocket(wsUrl);

    wsRef.current.onmessage = (event) => {
      const data = JSON.parse(event.data);
      
      if (data.event === 'job_status_update' && data.data.job_id === id) {
        fetchJob();
        toast.info(`Status updated: ${data.data.status.replace('_', ' ')}`);
      } else if (data.event === 'contractor_assigned' && data.data.job_id === id) {
        fetchJob();
        toast.success('Contractor assigned!');
      }
    };

    wsRef.current.onclose = () => {
      setTimeout(setupWebSocket, 3000);
    };
  };

  const fetchJob = async () => {
    try {
      const response = await axios.get(`${API_URL}/jobs/${id}`);
      setJob(response.data);
    } catch (error) {
      toast.error('Failed to load job');
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async (paymentIntentId) => {
    setProcessing(true);
    let attempts = 0;
    const maxAttempts = 10;

    const poll = async () => {
      try {
        const response = await axios.get(`${API_URL}/payments/status/${paymentIntentId}`);
        
        if (response.data.status === 'authorized' || response.data.status === 'captured') {
          setPaymentSuccess(true);
          setProcessing(false);
          toast.success('Payment successful! Searching for contractors...');
          await fetchJob();
          navigate(`/jobs/${id}`, { replace: true });
        } else if (response.data.status === 'requires_action') {
          // 3DS in progress - keep polling
          if (attempts < maxAttempts) {
            attempts++;
            setTimeout(poll, 2000);
          }
        } else if (attempts < maxAttempts) {
          attempts++;
          setTimeout(poll, 2000);
        } else {
          setProcessing(false);
          toast.error('Payment verification timed out');
        }
      } catch (error) {
        if (attempts < maxAttempts) {
          attempts++;
          setTimeout(poll, 2000);
        } else {
          setProcessing(false);
          toast.error('Failed to verify payment');
        }
      }
    };

    poll();
  };

  const handlePaymentSuccess = async (paymentIntent) => {
    setShowPaymentModal(false);
    setPaymentSuccess(true);
    toast.success('Payment successful! Searching for contractors...');
    
    // Check payment status to trigger dispatch
    try {
      await axios.get(`${API_URL}/payments/status/${paymentIntent.id}`);
      await fetchJob();
    } catch (error) {
      console.error('Error checking payment status:', error);
    }
  };

  const handlePayment = () => {
    setShowPaymentModal(true);
  };

  const handleSearchAgain = async () => {
    setSearchingAgain(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/jobs/${id}/search-again`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success('Searching for contractors again...');
        await fetchJob();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restart search');
    } finally {
      setSearchingAgain(false);
    }
  };

  const getCurrencySymbol = (currency) => {
    const symbols = { 'GBP': '£', 'USD': '$', 'EUR': '€' };
    return symbols[currency] || currency;
  };

  const getStatusTimeline = () => {
    const statuses = [
      { key: 'requested', label: 'Requested', icon: Clock },
      { key: 'searching', label: 'Searching', icon: Loader2 },
      { key: 'offered', label: 'Offered', icon: Loader2 },
      { key: 'accepted', label: 'Accepted', icon: CheckCircle },
      { key: 'en_route', label: 'En Route', icon: Navigation },
      { key: 'arrived', label: 'Arrived', icon: MapPin },
      { key: 'in_progress', label: 'In Progress', icon: Loader2 },
      { key: 'completed', label: 'Completed', icon: CheckCircle },
    ];
    
    // Handle expired states
    if (job?.status === 'expired_no_contractor' || job?.status === 'expired') {
      return { 
        statuses: [
          { key: 'requested', label: 'Requested', icon: Clock },
          { key: 'searching', label: 'Searching', icon: Loader2 },
          { key: 'expired', label: 'No Contractor Found', icon: XCircle }
        ], 
        currentIndex: 2 
      };
    }
    
    const currentIndex = statuses.findIndex(s => s.key === job?.status);
    return { statuses, currentIndex };
  };

  const isExpired = job?.status === 'expired_no_contractor' || job?.status === 'expired';
  const canSearchAgain = isExpired && job?.payment_status === 'paid';

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-black border-t-transparent"></div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <p className="text-neutral-600">Job not found</p>
      </div>
    );
  }

  const { statuses, currentIndex } = getStatusTimeline();
  const isSearching = ['searching', 'offered'].includes(job.status);
  const hasContractor = job.contractor || job.contractor_id;

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Header */}
      <header className="bg-black text-white sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-neutral-800 rounded-full">
            <ChevronLeft className="w-6 h-6" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold">{job.category}</h1>
            <p className="text-sm text-neutral-400">{job.subcategory}</p>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* EXPIRED - No Contractor Found */}
        {canSearchAgain && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-8 mb-6"
          >
            <div className="text-center">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-10 h-10 text-amber-600" />
              </div>
              <h2 className="text-2xl font-bold text-amber-800 mb-2">No Contractors Available</h2>
              <p className="text-amber-700 mb-4">
                {job.expired_reason || "We couldn't find a contractor for your job right now."}
              </p>
              <p className="text-sm text-amber-600 mb-6">
                Your payment is secure. Tap below to search again.
              </p>
              <Button
                onClick={handleSearchAgain}
                disabled={searchingAgain}
                className="bg-amber-600 hover:bg-amber-700 text-white px-8 h-12"
                data-testid="search-again-btn"
              >
                {searchingAgain ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Searching...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-5 h-5 mr-2" />
                    Search Again
                  </>
                )}
              </Button>
            </div>
          </motion.div>
        )}

        {/* Searching Animation */}
        {isSearching && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-blue-600 text-white rounded-2xl p-8 mb-6 text-center"
          >
            <div className="relative w-20 h-20 mx-auto mb-4">
              <div className="absolute inset-0 rounded-full border-4 border-white/30 animate-ping"></div>
              <div className="absolute inset-0 rounded-full border-4 border-white/50 animate-pulse"></div>
              <div className="absolute inset-2 rounded-full bg-white/20 flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin" />
              </div>
            </div>
            <h2 className="text-2xl font-bold mb-2">Finding the Best Contractor</h2>
            <p className="text-white/80 mb-2">Contacting nearby contractors...</p>
            <p className="text-sm text-white/60">This usually takes under 2 minutes</p>
            {job.search_expires_at && (
              <p className="text-xs text-white/50 mt-2">
                Searching until {new Date(job.search_expires_at).toLocaleTimeString()}
              </p>
            )}
          </motion.div>
        )}

        {/* Payment Success / Confirmation */}
        {(paymentSuccess || job.payment_status === 'paid') && job.status === 'requested' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-green-50 border-2 border-green-200 rounded-2xl p-8 mb-6 text-center"
          >
            <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-green-800 mb-2">Payment Confirmed!</h3>
            <p className="text-green-700">We're now searching for the best contractor near you.</p>
          </motion.div>
        )}

        {/* Verifying Payment */}
        {processing && searchParams.get('session_id') && (
          <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-8 mb-6 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent mx-auto mb-4"></div>
            <h3 className="text-xl font-bold text-blue-800 mb-2">Verifying Payment...</h3>
            <p className="text-blue-600">Please wait while we confirm your payment.</p>
          </div>
        )}

        {/* Contractor Card */}
        {job.contractor && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl border border-neutral-200 p-6 mb-6 shadow-sm"
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="w-16 h-16 bg-black rounded-full flex items-center justify-center">
                {job.contractor.photo ? (
                  <img src={job.contractor.photo} alt="" className="w-full h-full rounded-full object-cover" />
                ) : (
                  <User className="w-8 h-8 text-white" />
                )}
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold">{job.contractor.name}</h3>
                <div className="flex items-center gap-2 text-sm text-neutral-600">
                  <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                  <span>{job.contractor.rating?.toFixed(1) || '5.0'}</span>
                  <span>•</span>
                  <span>{job.contractor.jobs_completed || 0} jobs completed</span>
                </div>
              </div>
            </div>
            
            <div className="flex gap-3">
              <Button
                onClick={() => navigate(`/jobs/${id}/messages`)}
                className="flex-1 bg-black hover:bg-neutral-800 text-white"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                Message
              </Button>
              {job.contractor.phone && (
                <a
                  href={`tel:${job.contractor.phone}`}
                  className="flex items-center justify-center px-6 bg-green-600 hover:bg-green-500 text-white rounded-lg"
                >
                  <Phone className="w-5 h-5" />
                </a>
              )}
            </div>
          </motion.div>
        )}

        {/* Status Timeline */}
        {job.status !== 'cancelled' && job.status !== 'expired' && currentIndex >= 0 && (
          <div className="bg-white rounded-2xl border border-neutral-200 p-6 mb-6">
            <h3 className="font-bold mb-4">Job Progress</h3>
            <div className="relative">
              {statuses.slice(0, currentIndex === statuses.length - 1 ? undefined : currentIndex + 2).map((status, index) => {
                const Icon = status.icon;
                const isActive = index === currentIndex;
                const isCompleted = index < currentIndex;
                
                return (
                  <div key={status.key} className="flex items-center gap-4 mb-4 last:mb-0">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      isCompleted ? 'bg-green-500' :
                      isActive ? 'bg-blue-600' : 'bg-neutral-200'
                    }`}>
                      <Icon className={`w-5 h-5 ${
                        isCompleted || isActive ? 'text-white' : 'text-neutral-400'
                      } ${isActive && (status.key === 'searching' || status.key === 'in_progress') ? 'animate-spin' : ''}`} />
                    </div>
                    <div className="flex-1">
                      <p className={`font-medium ${
                        isActive ? 'text-blue-600' : isCompleted ? 'text-green-600' : 'text-neutral-400'
                      }`}>
                        {status.label}
                      </p>
                    </div>
                    {isCompleted && <CheckCircle className="w-5 h-5 text-green-500" />}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Job Details */}
        <div className="bg-white rounded-2xl border border-neutral-200 p-6 mb-6">
          <h3 className="font-bold mb-4">Job Details</h3>
          
          <div className="space-y-4">
            <div>
              <p className="text-sm text-neutral-500 mb-1">Description</p>
              <p className="text-neutral-800">{job.description}</p>
            </div>
            
            <div>
              <p className="text-sm text-neutral-500 mb-1">Urgency</p>
              <p className="text-neutral-800 capitalize">{job.urgency}</p>
            </div>
            
            {job.address_text && (
              <div>
                <p className="text-sm text-neutral-500 mb-1">Location</p>
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-neutral-400" />
                  <p className="text-neutral-800">{job.address_text}</p>
                </div>
              </div>
            )}
            
            <div>
              <p className="text-sm text-neutral-500 mb-1">Created</p>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-neutral-400" />
                <p className="text-neutral-800">
                  {new Date(job.created_at).toLocaleString('en-GB', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Pricing - Different views for customer vs contractor */}
        <div className="bg-white rounded-2xl border border-neutral-200 p-6 mb-6">
          <h3 className="font-bold mb-4">
            {user?.role === 'contractor' ? 'Your Earnings' : 'Pricing'}
          </h3>
          
          {/* Customer View - Simple total (no platform fee) */}
          {user?.role !== 'contractor' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-neutral-600">Total to pay</span>
                <span className="text-3xl font-bold">
                  {getCurrencySymbol(job.currency)}{job.estimated_price.toFixed(2)}
                </span>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                <span className="text-sm text-green-700 font-medium">✓ No platform fees • You only pay for the work</span>
              </div>
            </div>
          )}
          
          {/* Contractor View - Full earnings breakdown */}
          {user?.role === 'contractor' && (
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-neutral-500">Job total (customer pays)</span>
                <span className="font-medium">{getCurrencySymbol(job.currency)}{job.estimated_price.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-neutral-500">ZOLT platform fee (6%)</span>
                <span className="text-red-500">-{getCurrencySymbol(job.currency)}{(job.estimated_price * 0.06).toFixed(2)}</span>
              </div>
              <hr className="border-neutral-200" />
              <div className="flex justify-between items-center">
                <span className="font-semibold">Your payout</span>
                <span className="text-2xl font-bold text-green-600">
                  {getCurrencySymbol(job.currency)}{(job.estimated_price * 0.94).toFixed(2)}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Payment Button */}
        {job.status === 'requested' && user?.role === 'customer' && job.estimated_price > 0 && 
         !paymentSuccess && job.payment_status !== 'paid' && job.payment_status !== 'authorized' && (
          <Button
            onClick={handlePayment}
            disabled={processing}
            className="w-full bg-black hover:bg-neutral-800 text-white rounded-xl h-14 text-lg"
            data-testid="pay-now-btn"
          >
            <CreditCard className="w-5 h-5 mr-2" />
            {processing ? 'Processing...' : `Pay ${getCurrencySymbol(job.currency)}${job.estimated_price.toFixed(2)}`}
          </Button>
        )}

        {/* Embedded Payment Modal */}
        <EmbeddedPayment
          job={job}
          open={showPaymentModal}
          onOpenChange={setShowPaymentModal}
          onPaymentSuccess={handlePaymentSuccess}
        />

        {/* Completed State */}
        {job.status === 'completed' && (
          <div className="bg-green-50 border border-green-200 rounded-2xl p-6 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-green-800 mb-2">Job Completed!</h3>
            <p className="text-green-700 mb-4">Thank you for using ZOLT</p>
            {job.contractor && (
              <Button
                onClick={() => navigate(`/rate-contractor/${id}`)}
                className="bg-green-600 hover:bg-green-500 text-white"
              >
                <Star className="w-5 h-5 mr-2" />
                Rate {job.contractor.name}
              </Button>
            )}
          </div>
        )}

        {/* Cancelled/Expired State */}
        {['cancelled', 'expired'].includes(job.status) && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-red-800 mb-2">
              {job.status === 'cancelled' ? 'Job Cancelled' : 'Job Expired'}
            </h3>
            <p className="text-red-700 mb-4">
              {job.status === 'cancelled' 
                ? 'This job has been cancelled' 
                : 'No contractors were available. Please try again.'}
            </p>
            <Button
              onClick={() => navigate('/create-job')}
              className="bg-black hover:bg-neutral-800 text-white"
            >
              Request Another Zolt
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default JobDetailPage;
